import React from 'react';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import { AppBar, CircularProgress, createStyles, FormControl, FormControlLabel, Grid, IconButton, MenuItem, Select, Theme, Toolbar, Typography, withStyles, WithStyles } from '@material-ui/core';
import { ErrorData, HostType, LicenseType, validateHostId, initializeHostIdHelperText } from './ActivateLicenseData';
import { LinesData } from './DealsContext';
import { DeltaXMLColors, MyDeltaColors } from './styling/DeltaColors';
import EulaCheckBox, { EulaAgreedPreviously } from './EulaCheckBox';
import { DTFormatter } from '../general/datetime-formatter/DTFormatter';
import GetAppIcon from '@material-ui/icons/GetApp';
import { EulaCheckBoxData, HostData, HostDataRow, SystemActivationData } from './ActivateLicense';
import { NavigatorData, ProductId } from './NavigatorData';
import { EvalStatus } from './RequestEvaluation';
import CloseIcon from '@material-ui/icons/Close';
import { ProductsDataSet } from './DownloadProductData';

const styles = (theme: Theme) =>
  createStyles({
    appBar: {
      alignItems: "left",
      backgroundColor: DeltaXMLColors.OXFORD_BLUE,
    },
    hostIdTextField: {
      margin: theme.spacing(1),
      minWidth: 260,
    },
    hostForm: {
      backgroundColor: DeltaXMLColors.CONCRETE,
      borderRadius: 5,
      margin: theme.spacing(1),
      minWidth: 125,
    },
    select: {
      marginLeft: '5px',
      marginRight: '5px'
    },
    linkStyle: {
      color: DeltaXMLColors.FOUNTAIN_BLUE,
      marginTop: '10px'
    },
    evalDialog: {
      minwidth: 1000,
    },
    boldText: {
      fontWeight: 'bold'
    },
    productHeader: {
      marginTop: '20px',
      marginLeft: '10px',
      color: DeltaXMLColors.CELERY
    },
    productHeaderIcon: {
      marginTop: '5px',
    },
    progress: {
      margin: '8px 0px 0px 10px',
      color: DeltaXMLColors.OXFORD_BLUE,
    },
    statusMessage: {
      marginLeft: '50px',
      marginTop: '8px'
    },
    closeIcon: {
      color: MyDeltaColors.WHITE,
      marginTop: '15px',
      float: 'right'
    }
  });


export interface ActivateEvalLicenseProps extends WithStyles<typeof styles> {
  dealId: string,
  line: LinesData,
  downloadProductsData: ProductsDataSet,
  dialogOpen: boolean,
  activateStatus: EvalStatus,
  dateFormat: string,
  handleCloseDialog: () => void,
  handleActivateAndDownload: (data: SystemActivationData) => void
}

function ActivateEvalLicenseDialog(props: ActivateEvalLicenseProps) {
  const { classes, dealId, line, downloadProductsData, dialogOpen, handleCloseDialog, dateFormat, handleActivateAndDownload, activateStatus } = props;
  const [hostType, setHostType] = React.useState((line.allowedHostIds.length !== 1 ? "" : line.allowedHostIds[0]) as HostType);
  const [hostId, setHostId] = React.useState('');
  const [isEulaAccepted, setEulaAccepted] = React.useState(false);
  const [displayedLicenseId, setDisplayedLicenseId] = React.useState('');
  const [eulaData, setEulaData] = React.useState<EulaCheckBoxData>({ previouslyAgreed: EulaAgreedPreviously.unknown, isCustom: false })

  const licenseType: LicenseType = line.licenseModel as LicenseType;
  const lineSupportExpiry: string = DTFormatter.getFormattedDate(line.supportExpiry, dateFormat);

  const initialErrorData: ErrorData = {
    hostId: (licenseType !== LicenseType.server_worldbox && licenseType !== LicenseType.server_boeing && licenseType !== LicenseType.server_unlocked) ? initializeHostIdHelperText(hostType) : null,
    hostId2: null,
    hostId3: null,
    port: null
  }

  const [errorData, setErrorData] = React.useState(initialErrorData);
  let isFormValid: boolean = fieldsWithNoErrors(errorData);

  const handleHostTypeChange = (hostTypeArg: HostType) => {
    // as a convenience (because in most cases all host types will be the same)
    // if the first host type is changed, change all other host types:
    setHostType(hostTypeArg);
    setHostId('');

    const errMessage = initializeHostIdHelperText(hostTypeArg);
    setErrorData({
      ...errorData,
      hostId: errMessage,
      hostId2: null,
      hostId3: null
    });
  };

  const handleEulaDataRequest = (data: EulaCheckBoxData) => {
    setEulaData(data);
  }

  const handleHostIdChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateHostId(event.target.value);
  }

  const updateHostId = (hostIdArg: string) => {
    setHostId(hostIdArg);
    validateCurrentHostIds(false, { row: HostDataRow.none }, [hostIdArg]);
  }

  const validateCurrentHostIds = (checkDuplicates: boolean, hostData: HostData, allHostIds = [hostId]) => {
    const ht1 = (hostData.row === HostDataRow.hostRow1 ? hostData.value : hostType) as HostType;
    const errorMsg1 = validateHostId(ht1, allHostIds[0], allHostIds, checkDuplicates);

    setErrorData(prevErrData => {
      const newErrData = { ...prevErrData };
      newErrData["hostId"] = errorMsg1;
      return newErrData;
    });
  }

  const onGetLicenseClick = () => {
    let hostObject: SystemActivationData;
    hostObject = { hostType: hostType, hostType2: HostType.na, hostType3: HostType.na, hostId: hostId, hostId2: "", hostId3: "", licenseId: displayedLicenseId };

    setEulaAccepted(true);
    setEulaData(prevData => {
      const newData = { ...prevData };
      newData.previouslyAgreed = EulaAgreedPreviously.yes;
      return newData;
    });
    handleActivateAndDownload(hostObject);
  }

  const handleEulaCheckboxChange = () => {
    setEulaAccepted(!isEulaAccepted)
  }

  const handleLicenseAccepted = (licenseId: string) => {
    setDisplayedLicenseId(licenseId);
  }

  const noHostIdsRequired = line.allowedHostIds.length === 0;
  const treatAsIfEulaAccepted = (isEulaAccepted || eulaData.isCustom || eulaData.previouslyAgreed === EulaAgreedPreviously.yes);


  const productName = line.product;
  const baseProductName = productName.includes(ProductId.deltaJson) || productName.includes(ProductId.xmlDataCompare) ? productName : productName.substring(0, line.product.lastIndexOf(' '));
  const productIcon = NavigatorData.DownloadProductSections.find(
    item => item.id === baseProductName)?.icon;

  const showDownloads = activateStatus === EvalStatus.Success;

  const evalDownloadURL = (productId: string) => {
    const products = downloadProductsData.products;
    const currentProduct = products.find(prod => prod.product === productId);
    const currentDownload = currentProduct?.downloads.find(download => download.current_release === true && (download.filename.includes("_j.zip") || download.filename.includes("DeltaJSON") || download.filename.includes("Data-Compare")));
    const downloadUrl = currentDownload?.download_url;
    
    window.location.href = downloadUrl!;
  }

  let requestBarData = "";
  let showProgressCircle = false;
  if (activateStatus === EvalStatus.RequestOngoing) {
    showProgressCircle = true;
    requestBarData = "Getting your license...";
  } else if (activateStatus === EvalStatus.Error || activateStatus === EvalStatus.InternalServerError) {
    showProgressCircle = false;
    requestBarData = "Error encountered when getting your license";
  } else if (activateStatus === EvalStatus.Success) {
    showProgressCircle = false;
    requestBarData = "License download successful";
  } 
  requestBarData = requestBarData === '' ? requestBarData : '[' + requestBarData + ']';

  return (
    <React.Fragment>
      <Dialog open={dialogOpen} onClose={handleCloseDialog} className={classes.evalDialog}>
        <AppBar className={classes.appBar} position="static">
          <Toolbar>
            <Grid container>
              <Grid item xs={1} className={classes.productHeaderIcon}>
                {productIcon}
              </Grid>
              <Grid item xs={10}>
                <Typography variant="h3" className={classes.productHeader}>
                  {productName}
                </Typography>
              </Grid>
              <Grid item xs={1}>
                <IconButton onClick={() => handleCloseDialog()}>
                  <CloseIcon fontSize='large' className={classes.closeIcon}/>
                </IconButton>
              </Grid>
            </Grid>
          </Toolbar>
        </AppBar>
        <br />
        <DialogContent>
          <Typography variant="h5" className={classes.boldText}>
            Activate your {line.product} License
          </Typography>
          <br />
          <DialogContentText>
            <Typography variant="body1" color="textSecondary">
              To activate your license you must enter the Host Id, IP Address, or MAC Address to which the license will be locked.
            </Typography>
            <Typography variant="body1" color="textSecondary" style={{fontStyle: 'italic'}}>
              (eg. If Host Type is Hostname, your Host ID might be joebloggs-companyname).
            </Typography>
          </DialogContentText>
          <Grid container>
            <Grid item xs={3}>
              <FormControlLabel
                control={
                  <FormControl className={classes.hostForm}>
                    <Select className={classes.select} value={hostType} onChange={event => handleHostTypeChange(event.target.value as HostType)} displayEmpty>
                      {line.allowedHostIds.length > 1 &&
                        <MenuItem value="" disabled>Select host type</MenuItem>
                      }
                      {line.allowedHostIds?.filter(type => type !== HostType.ethernetx3)
                        .filter(type => type !== HostType.hostNamex3)
                        .filter(type => type !== HostType.internetx3)
                        .map(type =>
                          <MenuItem key={type} value={type} style={{ color: DeltaXMLColors.OXFORD_BLUE }}>{type}</MenuItem>
                        )}
                    </Select>
                  </FormControl>
                }
                label="Host Type"
                labelPlacement="top"
              />
            </Grid>
            <Grid item xs={5}>
              <FormControlLabel
                control={<TextField className={classes.hostIdTextField} value={hostId} onChange={handleHostIdChange}
                  helperText={errorData.hostId !== null ? errorData.hostId : "Valid Host Id"} error={errorData.hostId !== null} />}
                label="Host Id"
                labelPlacement="top"
              />
            </Grid>
            <Grid item xs={4}></Grid>
          </Grid>
          
          <Grid style={{ height: '30px' }}>
            <EulaCheckBox dealId={dealId} eulaRequestData={eulaData} lineLicenseType={licenseType} lineExpiry={lineSupportExpiry} isEulaAccepted={isEulaAccepted} handleEulaCheckboxChange={handleEulaCheckboxChange} handleLicenseAccepted={handleLicenseAccepted} handleEulaDataRequest={handleEulaDataRequest} />
          </Grid>
          <br />
          <Grid container style={{ marginBottom: '20px' }}>
            <Grid>
              <Button onClick={onGetLicenseClick} variant="contained" color="primary"
                disabled={!(isFormValid && treatAsIfEulaAccepted) || (noHostIdsRequired && !treatAsIfEulaAccepted)}>
                Get License <GetAppIcon />
              </Button>
            </Grid>
            <Grid>
              <Typography className={classes.statusMessage} variant="body2" color="textSecondary">
                {requestBarData}
              </Typography>
            </Grid>
            <Grid>
              {showProgressCircle &&
                <CircularProgress className={classes.progress} variant="indeterminate" size="20px" />
              }
            </Grid>
          </Grid>
          {showDownloads &&
            <React.Fragment>
              <br />
              <Typography variant="h5" className={classes.boldText}>
                Product files ready To download
              </Typography>
              <br />
              <Typography variant="body1" color="textSecondary" style={{marginBottom: '5px'}}>
                Your {line.product} license is ready to download. Click the button below to get started.
              </Typography>
              <Button onClick={() => evalDownloadURL(productName)} variant="contained" color="primary">
                Download product files <GetAppIcon />
              </Button>
              <br />
              <br />
              <br />
              <Typography variant="h6" className={classes.boldText}>
                Support & Next Steps
              </Typography>
              <Typography variant="body1" color="textSecondary">
                <a className={classes.linkStyle} href='https://www.youtube.com/watch?v=ha551WTvjjU&t=1s&ab_channel=DeltaXML' target="_blank" rel="noreferrer">Watch the “Running Your First Comparison or Merge Operation” video</a>
              </Typography>
              <Typography variant="body1" color="textSecondary">
                <a className={classes.linkStyle} href='https://deltaxml.atlassian.net/servicedesk/customer/portal/5' target="_blank" rel="noreferrer">Submit a support ticket</a>
              </Typography>
              <Typography variant="body1" color="textSecondary" style={{marginBottom: '20px'}}>
                <a className={classes.linkStyle} href='https://calendly.com/roger-heatley' target="_blank" rel="noreferrer">Book an onboarding session with our experts</a>
              </Typography>
            </React.Fragment>
          }
        </DialogContent>
      </Dialog>
    </React.Fragment>
  );
}
export default withStyles(styles)(ActivateEvalLicenseDialog);

const fieldsWithNoErrors = (errorData: ErrorData): boolean => {
  let fieldsWithNoErrors: boolean = false;
  const otherError = Object.entries(errorData)
    .find((property) => {
      const [name, value] = property;
      return value !== null;
    });

  if (!otherError) {
    fieldsWithNoErrors = true;
  }
  return fieldsWithNoErrors;
}